import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { createKitchenDining } from "../../../redux/actions/kitchenDiningActions";
import { toast } from "react-hot-toast";
import CameraCapture from "../../../components/shared/CameraCapture";
import { useLanguage } from "../../../context/LanguageContext";
import {
  ArrowLeft,
  Save,
  AlertCircle,
  CheckCircle,
  Upload,
  Camera,
  FileText,
  Home,
  Droplet,
  Wind,
  Calendar,
  Flame,
  Sparkles,
  Package,
  Shield,
  Thermometer,
  Wrench,
} from "lucide-react";

const SectionDKitchenDining = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const dispatch = useDispatch();
  const { t } = useLanguage();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [cameraField, setCameraField] = useState(null);
  const [previewImages, setPreviewImages] = useState({
    d2Photo: null,
    d3Photo: null,
    d5Document: null,
    d6Document: null,
  });

  const ratingOptions = [t.global.rating_good, t.global.rating_satisfactory, t.global.rating_poor];

  const [form, setForm] = useState({
    d1: "",
    d2: "",
    d2Photo: null,
    d3: "",
    d3Photo: null,
    d4: "",
    d5: "",
    d5Document: null,
    d6: "",
    d6Document: null,
    d7: "",
    d8: "",
    remarks: "",
  });

  const [errors, setErrors] = useState({});

  // Load saved draft if exists
  useEffect(() => {
    const loadSavedData = () => {
      try {
        const savedData = localStorage.getItem("survey_section_d");
        if (savedData) {
          const parsedData = JSON.parse(savedData);
          const { timestamp, canteenInfo, ...formData } = parsedData;
          setForm(formData);
        }
      } catch (err) {
        console.error("Error loading saved data:", err);
      }
    };

    loadSavedData();
  }, []);

  // Auto-save to localStorage
  useEffect(() => {
    const timer = setTimeout(() => {
      if (Object.values(form).some((v) => v !== "" && v !== null)) {
        localStorage.setItem("survey_section_d", JSON.stringify(form));
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [form]);

  const handleChange = (e) => {
    const { name, value, type, files } = e.target;

    if (type === "file") {
      const file = files[0];

      // Validate file size
      if (file && file.size > 5 * 1024 * 1024) {
        setErrors((prev) => ({
          ...prev,
          [name]: t.global.err_file_size,
        }));
        return;
      }

      // Validate file type
      const allowedTypes = [
        "application/pdf",
        "image/jpeg",
        "image/jpg",
        "image/png",
      ];

      if (file && !allowedTypes.includes(file.type)) {
        setErrors((prev) => ({
          ...prev,
          [name]: t.global.err_file_type_long,
        }));
        return;
      }

      // Store file in form
      setForm((prev) => ({ ...prev, [name]: file }));

      if (file && file.type.startsWith("image/")) {
        setPreviewImages((prev) => ({
          ...prev,
          [name]: URL.createObjectURL(file),
        }));
      }

      // Clear file error
      if (errors[name]) {
        setErrors((prev) => ({ ...prev, [name]: undefined }));
      }
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }

    // Clear error when user changes input
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Required field validations for all radio buttons
    if (!form.d1) newErrors.d1 = t.global.err_a1;
    if (!form.d2) newErrors.d2 = t.global.err_a1;
    if (!form.d3) newErrors.d3 = t.global.err_a1;
    if (!form.d4) newErrors.d4 = t.global.err_a1;
    if (!form.d5) newErrors.d5 = t.global.err_a1;
    if (!form.d6) newErrors.d6 = t.global.err_a1;
    if (!form.d7) newErrors.d7 = t.global.err_a1;
    if (!form.d8) newErrors.d8 = t.global.err_a1;

    // File validation only if file IS uploaded (optional but recommended)
    if (form.d2Photo && form.d2Photo.size > 5 * 1024 * 1024) {
      newErrors.d2Photo = t.global.err_file_size;
    }
    if (form.d2Photo) {
      const allowedTypes = [
        "application/pdf",
        "image/jpeg",
        "image/jpg",
        "image/png",
      ];
      if (!allowedTypes.includes(form.d2Photo.type)) {
        newErrors.d2Photo = t.global.err_file_type_long;
      }
    }

    if (form.d3Photo && form.d3Photo.size > 5 * 1024 * 1024) {
      newErrors.d3Photo = t.global.err_file_size;
    }
    if (form.d3Photo) {
      const allowedTypes = [
        "application/pdf",
        "image/jpeg",
        "image/jpg",
        "image/png",
      ];
      if (!allowedTypes.includes(form.d3Photo.type)) {
        newErrors.d3Photo = t.global.err_file_type_long;
      }
    }

    if (form.d5Document && form.d5Document.size > 5 * 1024 * 1024) {
      newErrors.d5Document = t.global.err_file_size;
    }
    if (form.d5Document) {
      const allowedTypes = [
        "application/pdf",
        "image/jpeg",
        "image/jpg",
        "image/png",
      ];
      if (!allowedTypes.includes(form.d5Document.type)) {
        newErrors.d5Document = t.global.err_file_type_long;
      }
    }

    if (form.d6Document && form.d6Document.size > 5 * 1024 * 1024) {
      newErrors.d6Document = t.global.err_file_size;
    }
    if (form.d6Document) {
      const allowedTypes = [
        "application/pdf",
        "image/jpeg",
        "image/jpg",
        "image/png",
      ];
      if (!allowedTypes.includes(form.d6Document.type)) {
        newErrors.d6Document = t.global.err_file_type_long;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      // Get latitude & longitude automatically
      let latitude = "";
      let longitude = "";

      if (navigator.geolocation) {
        await new Promise((resolve) => {
          navigator.geolocation.getCurrentPosition(
            (position) => {
              latitude = position.coords.latitude;
              longitude = position.coords.longitude;
              resolve();
            },
            (error) => {
              console.warn("Geolocation error:", error);
              resolve(); // still proceed without location
            },
            { timeout: 10000, enableHighAccuracy: true },
          );
        });
      }

      // Create FormData for API
      const fd = new FormData();
      fd.append("reporting_date", new Date().toISOString().split("T")[0]);
      fd.append("district_id", state?.district_id);
      fd.append("municipality_id", state?.municipality_id);
      fd.append("ward_id", state?.ward_id);
      fd.append("area_id", state?.area_id);
      fd.append("canteen_type_id", state?.canteen_type_id);
      fd.append("latitude", latitude || "0");
      fd.append("longitude", longitude || "0");
      fd.append("reporting_date", new Date().toISOString().split("T")[0]);

      // Section D data mapping
      const yesNoMap = { [t.global.yes]: "1", [t.global.no]: "2" };
      const ratingMap = {
        [t.global.rating_good]: "1",
        [t.global.rating_satisfactory]: "2",
        [t.global.rating_poor]: "3",
      };

      fd.append("kitch_spce_D1", yesNoMap[form.d1]);
      fd.append("kitch_spce_D2", yesNoMap[form.d2]);
      if (form.d2 === t.global.yes && form.d2Photo) {
        fd.append("kitch_spce_D2_upload", form.d2Photo);
      }

      fd.append("kitch_spce_D3", yesNoMap[form.d3]);
      if (form.d3 === t.global.yes && form.d3Photo) {
        fd.append("kitch_spce_D3_upload", form.d3Photo);
      }

      fd.append("kitch_spce_D4", yesNoMap[form.d4]);
      fd.append("kitch_spce_D5", yesNoMap[form.d5]);
      if (form.d5 === t.global.yes && form.d5Document) {
        fd.append("kitch_spce_D5_upload", form.d5Document);
      }

      fd.append("kitch_spce_D6", yesNoMap[form.d6]);
      if (form.d6 === t.global.yes && form.d6Document) {
        fd.append("kitch_spce_D6_upload", form.d6Document);
      }

      fd.append("kitch_spce_D7", yesNoMap[form.d7]);
      fd.append("kitch_spce_D8", ratingMap[form.d8]);
      if (form.remarks) {
        fd.append("kitchen_remarks", form.remarks);
      }

      fd.append("current_section", "kitchenSpace");

      // Dispatch the action
      await dispatch(createKitchenDining(fd));

      // ✅ Check if this section is partial (missing optional uploads when YES is selected)
      const isPartial =
        (form.d2 === t.global.yes && !form.d2Photo) ||
        (form.d3 === t.global.yes && !form.d3Photo) ||
        (form.d5 === t.global.yes && !form.d5Document) ||
        (form.d6 === t.global.yes && !form.d6Document);

      // ✅ Save to localStorage with partial flag
      const sectionData = {
        ...form,
        isPartial: isPartial,
        submittedAt: new Date().toISOString(),
      };

      localStorage.setItem("survey_section_d", JSON.stringify(sectionData));

      // ✅ Mark Section D as submitted (could be partial)
      const completedSections = JSON.parse(
        localStorage.getItem("completedSections") || "{}",
      );
      completedSections.d = true;
      localStorage.setItem(
        "completedSections",
        JSON.stringify(completedSections),
      );

      // Show success message
      setShowSuccess(true);

      // Auto-navigate after 2 seconds
      setTimeout(() => {
        setShowSuccess(false);
        navigate("/survey/sections", { state });
      }, 2000);
    } catch (error) {
      console.error("Submission error:", error);

      // Save locally for offline
      const isPartial =
        (form.d2 === t.global.yes && !form.d2Photo) ||
        (form.d3 === t.global.yes && !form.d3Photo) ||
        (form.d5 === t.global.yes && !form.d5Document) ||
        (form.d6 === t.global.yes && !form.d6Document);

      const sectionData = {
        ...form,
        isPartial: isPartial,
        submittedAt: new Date().toISOString(),
        savedLocally: true,
      };

      localStorage.setItem("survey_section_d", JSON.stringify(sectionData));

      // Mark as completed locally
      const completedSections = JSON.parse(
        localStorage.getItem("completedSections") || "{}",
      );
      completedSections.d = true;
      localStorage.setItem(
        "completedSections",
        JSON.stringify(completedSections),
      );

      toast.error(t.global.alert_saved_locally);
      setIsSubmitting(false);
      navigate("/survey/sections", { state });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-4xl mx-auto">
        {/* Success Message Overlay */}
        {showSuccess && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl p-8 max-w-md w-full text-center animate-scale-in">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                {t.global.success_title}
              </h3>
              <p className="text-gray-600 mb-6">
                Section D submitted successfully.
              </p>
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="ml-3 text-gray-600">
                  {t.global.redirecting}
                </span>
              </div>
              <button
                onClick={() => {
                  setShowSuccess(false);
                  navigate("/survey/sections", { state });
                }}
                className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
              >
                {t.global.continue_now}
              </button>
            </div>
          </div>
        )}

        <div className="bg-white rounded-xl shadow p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-800">
                {t.modules.kitchenCumDining.sections.D.title}
              </h2>
              <p className="text-gray-600">
                {t.modules.kitchenCumDining.sections.D.subtitle}
              </p>
            </div>
            <div className="flex flex-col items-end gap-2">
              <div className="text-sm bg-indigo-100 text-indigo-800 px-3 py-1 rounded-full flex items-center">
                <Home size={14} className="mr-1" />
                Kitchen & Dining Survey
              </div>
              <div className="text-sm bg-green-100 text-green-800 px-3 py-1 rounded-full">
                {state?.area_name ? `${t.global.area_id}: ${state.area_name}` : t.global.na}
              </div>
            </div>
          </div>

          <div className="space-y-6">
            {/* D1 - Kitchen Cover */}
            <div className="border rounded-lg p-4 bg-blue-50 border-blue-200">
              <label className="block font-medium mb-3 flex items-center">
                <Shield size={18} className="mr-2" />
                D.1 {t.modules.kitchenCumDining.sections.D.questions.coveredKitchen}
                <span className="text-red-500 ml-1">*</span>
              </label>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-gray-800 font-medium mb-1">
                  {t.modules.kitchenCumDining.sections.D.hints.coveredKitchen.split(":")[0]}:
                </p>
                <ul className="text-sm text-gray-700 list-disc pl-5 space-y-1">
                  <li>Adequate coverage on all sides</li>
                  <li>Overhead shed/roof protection</li>
                  <li>Prevents entry of dirt, dust, pests</li>
                  <li>Proper ventilation maintained</li>
                </ul>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[t.global.yes, t.global.no].map((option) => (
                  <label
                    key={option}
                    className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      form.d1 === option
                        ? "bg-blue-100 border-blue-400 shadow-sm"
                        : "bg-white border-gray-200 hover:border-gray-300"
                    } ${errors.d1 ? "border-red-300" : ""}`}
                  >
                    <input
                      type="radio"
                      name="d1"
                      value={option}
                      checked={form.d1 === option}
                      onChange={handleChange}
                      className="mr-3 h-5 w-5"
                      required
                    />
                    <div className="flex items-center">
                      <span
                        className={`text-lg font-medium ${
                          form.d1 === option ? "text-blue-700" : "text-gray-700"
                        }`}
                      >
                        {option}
                      </span>
                    </div>
                  </label>
                ))}
              </div>
              {errors.d1 && (
                <p className="mt-2 text-sm text-red-600 flex items-center">
                  <AlertCircle size={14} className="mr-1" /> {errors.d1}
                </p>
              )}
            </div>

            {/* D2 - Kitchen Sink */}
            <div className="border rounded-lg p-4 bg-blue-50 border-blue-200">
              <label className="block font-medium mb-3 flex items-center">
                <Droplet size={18} className="mr-2" />
                D.2 {t.modules.kitchenCumDining.sections.D.questions.staffSink}
                <span className="text-red-500 ml-1">*</span>
              </label>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-amber-800 font-medium mb-1">
                  {t.modules.kitchenCumDining.sections.D.hints.staffSink.split(":")[0]}:
                </p>
                <ul className="text-sm text-amber-700 list-disc pl-5 space-y-1">
                  <li>Running water available</li>
                  <li>Soap for handwash provided</li>
                  <li>Proper tap functional</li>
                  <li>Outlet pipe connected</li>
                  <li>Connected to drainage system</li>
                </ul>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                {[t.global.yes, t.global.no].map((option) => (
                  <label
                    key={option}
                    className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      form.d2 === option
                        ? "bg-blue-100 border-blue-400 shadow-sm"
                        : "bg-white border-gray-200 hover:border-gray-300"
                    } ${errors.d2 ? "border-red-300" : ""}`}
                  >
                    <input
                      type="radio"
                      name="d2"
                      value={option}
                      checked={form.d2 === option}
                      onChange={handleChange}
                      className="mr-3 h-5 w-5"
                      required
                    />
                    <span
                      className={`text-lg font-medium ${
                        form.d2 === option ? "text-blue-700" : "text-gray-700"
                      }`}
                    >
                      {option}
                    </span>
                  </label>
                ))}
              </div>

              {errors.d2 && (
                <p className="mt-2 text-sm text-red-600 flex items-center">
                  <AlertCircle size={14} className="mr-1" /> {errors.d2}
                </p>
              )}
              {form.d2 === t.global.yes && (
                <div className="mt-6 p-4 bg-green-50 border-2 border-dashed border-green-300 rounded-lg space-y-5">
                  <label className="block text-sm font-medium text-gray-700">
                    Upload sink photo (Optional):
                  </label>

                  {/* Buttons */}
                  <div className="grid grid-cols-2 gap-4">
                    <button
                      type="button"
                      onClick={() => {
                        setCameraField("d2Photo");
                        setShowCamera(true);
                      }}
                      disabled={isSubmitting}
                      className="flex items-center justify-center gap-2 p-3 border border-green-300 bg-green-900/10 text-green-900 rounded-lg active:scale-95 active:bg-green-700 active:text-white transition-all duration-150 font-medium disabled:opacity-50"
                    >
                      <Camera size={18} />
                      {t.global.use_camera}
                    </button>

                    <label
                      htmlFor="d2Photo"
                      className={`flex items-center justify-center gap-2 p-3 border border-green-300 bg-green-900/10 text-green-900 rounded-lg active:scale-95 active:bg-green-700 active:text-white transition-all duration-150 cursor-pointer font-medium ${
                        isSubmitting ? "opacity-50 cursor-not-allowed" : ""
                      }`}
                    >
                      <Upload size={18} />
                      {t.global.upload_file}
                    </label>
                  </div>

                  {/* Hidden File Input */}
                  <input
                    type="file"
                    name="d2Photo"
                    id="d2Photo"
                    accept=".jpg,.jpeg,.png,.pdf"
                    onChange={handleChange}
                    className="hidden"
                    disabled={isSubmitting}
                  />

                  {/* Preview Box */}
                  <div
                    className={`border-2 border-dashed rounded-lg p-6 text-center transition ${
                      form.d2Photo
                        ? "border-yellow-400 bg-yellow-50"
                        : "border-gray-300 bg-gray-50"
                    }`}
                  >
                    {form.d2Photo ? (
                      <div className="space-y-3">
                        {/* Image Preview */}
                        {previewImages?.d2Photo && (
                          <img
                            src={previewImages.d2Photo}
                            alt="Preview"
                            className="max-h-48 mx-auto rounded-lg shadow"
                          />
                        )}

                        {/* File Name + Remove */}
                        <div className="flex items-center justify-between">
                          <p className="text-gray-700 font-medium text-sm break-all">
                            {form.d2Photo?.name}
                          </p>

                          <button
                            type="button"
                            onClick={() => {
                              setForm((prev) => ({ ...prev, d2Photo: null }));
                              setPreviewImages((prev) => ({
                                ...prev,
                                d2Photo: null,
                              }));
                            }}
                            className="ml-4 text-red-600 px-3 py-1 rounded-md hover:bg-red-600 hover:text-white transition text-xs font-bold"
                          >
                            {t.global.remove}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <p className="text-gray-400 italic text-sm">
                        {t.global.capture_hint}
                      </p>
                    )}
                  </div>

                  {/* Error */}
                  {errors?.d2Photo && (
                    <p className="text-sm text-red-600">{errors.d2Photo}</p>
                  )}
                </div>
              )}
            </div>

            {/* D3 - Handwashing Station */}
            <div className="border rounded-lg p-4 bg-blue-50 border-blue-200">
              <label className="block font-medium mb-3 flex items-center">
                <Wrench size={18} className="mr-2" />
                D.3 {t.modules.kitchenCumDining.sections.D.questions.beneficiaryHandwashing}
                <span className="text-red-500 ml-1">*</span>
              </label>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-amber-800 font-medium mb-1">
                  {t.modules.kitchenCumDining.sections.D.hints.beneficiaryHandwashing.split(":")[0]}:
                </p>
                <ul className="text-sm text-amber-700 list-disc pl-5 space-y-1">
                  <li>Running water available</li>
                  <li>Soap for handwash provided</li>
                  <li>Proper tap functional</li>
                  <li>Covered area with outlet pipe</li>
                  <li>Connected to drainage system</li>
                </ul>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                {[t.global.yes, t.global.no].map((option) => (
                  <label
                    key={option}
                    className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      form.d3 === option
                        ? "bg-blue-100 border-blue-400 shadow-sm"
                        : "bg-white border-gray-200 hover:border-gray-300"
                    } ${errors.d3 ? "border-red-300" : ""}`}
                  >
                    <input
                      type="radio"
                      name="d3"
                      value={option}
                      checked={form.d3 === option}
                      onChange={handleChange}
                      className="mr-3 h-5 w-5"
                      required
                    />
                    <div className="flex items-center">
                      <span
                        className={`text-lg font-medium ${
                          form.d3 === option ? "text-blue-700" : "text-gray-700"
                        }`}
                      >
                        {option}
                      </span>
                    </div>
                  </label>
                ))}
              </div>
              {errors.d3 && (
                <p className="mt-2 text-sm text-red-600 flex items-center">
                  <AlertCircle size={14} className="mr-1" /> {errors.d3}
                </p>
              )}

              {form.d3 === t.global.yes && (
                <div className="mt-6 p-4 bg-green-50 border-2 border-dashed border-green-300 rounded-lg space-y-5">
                  <label className="block text-sm font-medium mb-3 text-gray-700">
                    Upload handwashing station photo (Optional):
                  </label>

                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <button
                      type="button"
                      onClick={() => {
                        setCameraField("d3Photo");
                        setShowCamera(true);
                      }}
                      disabled={isSubmitting}
                      className="flex items-center justify-center gap-2 p-3 border border-green-300 bg-green-900/10 text-green-900 rounded-lg active:scale-95 active:bg-green-700 active:text-white transition-all duration-150 font-medium disabled:opacity-50"
                    >
                      <Camera size={18} /> {t.global.use_camera}
                    </button>

                    <label
                      htmlFor="d3Photo"
                      className={`flex items-center justify-center gap-2 p-3 border border-green-300 bg-green-900/10 text-green-900 rounded-lg active:scale-95 active:bg-green-700 active:text-white transition-all duration-150 cursor-pointer font-medium ${
                        isSubmitting ? "opacity-50 cursor-not-allowed" : ""
                      }`}
                    >
                      <Upload size={18} /> {t.global.upload_file}
                    </label>
                  </div>

                  <input
                    type="file"
                    name="d3Photo"
                    id="d3Photo"
                    accept=".jpg,.jpeg,.png,.pdf"
                    onChange={handleChange}
                    className="hidden"
                    disabled={isSubmitting}
                  />

                  <div
                    className={`border-2 border-dashed rounded-lg p-6 text-center transition ${
                      form.d3Photo
                        ? "border-yellow-400 bg-yellow-50"
                        : "border-gray-300 bg-gray-50"
                    }`}
                  >
                    {form.d3Photo ? (
                      <div className="space-y-3">
                        {previewImages.d3Photo && (
                          <img
                            src={previewImages.d3Photo}
                            alt="Preview"
                            className="max-h-48 mx-auto rounded-lg shadow"
                          />
                        )}

                        <div className="flex items-center justify-between">
                          <p className="text-gray-700 font-medium text-sm break-all">
                            {form.d3Photo.name}
                          </p>

                          <button
                            type="button"
                            onClick={() => {
                              setForm((prev) => ({ ...prev, d3Photo: null }));
                              setPreviewImages((prev) => ({
                                ...prev,
                                d3Photo: null,
                              }));
                            }}
                            className="ml-4 text-red-600 px-3 py-1 rounded-md hover:bg-red-600 hover:text-white transition text-xs font-bold"
                          >
                            {t.global.remove}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <p className="text-gray-400 italic text-sm">
                        {t.global.capture_hint}
                      </p>
                    )}
                  </div>

                  {errors.d3Photo && (
                    <p className="mt-2 text-sm text-red-600">
                      {errors.d3Photo}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* D4 - Kitchen Ventilation */}
            <div className="border rounded-lg p-4">
              <label className="block font-medium mb-3 flex items-center">
                <Wind size={18} className="mr-2" />
                D.4 {t.modules.kitchenCumDining.sections.D.questions.ventilation}
                <span className="text-red-500 ml-1">*</span>
              </label>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[t.global.yes, t.global.no].map((option) => (
                  <label
                    key={option}
                    className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      form.d4 === option
                        ? "bg-blue-100 border-blue-400 shadow-sm"
                        : "bg-white border-gray-200 hover:border-gray-300"
                    } ${errors.d4 ? "border-red-300" : ""}`}
                  >
                    <input
                      type="radio"
                      name="d4"
                      value={option}
                      checked={form.d4 === option}
                      onChange={handleChange}
                      className="mr-3 h-5 w-5"
                      required
                    />
                    <div className="flex items-center">
                      <span
                        className={`text-lg font-medium ${
                          form.d4 === option ? "text-blue-700" : "text-gray-700"
                        }`}
                      >
                        {option}
                      </span>
                    </div>
                  </label>
                ))}
              </div>
              {errors.d4 && (
                <p className="mt-2 text-sm text-red-600 flex items-center">
                  <AlertCircle size={14} className="mr-1" /> {errors.d4}
                </p>
              )}
            </div>

            {/* D5 - Deep Cleaning Schedule */}
            <div className="border rounded-lg p-4 bg-blue-50 border-blue-200">
              <label className="block font-medium mb-3 flex items-center">
                <Calendar size={18} className="mr-2" />
                D.5 {t.modules.kitchenCumDining.sections.D.questions.deepCleaning}
                <span className="text-red-500 ml-1">*</span>
              </label>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-gray-800 font-medium mb-1">
                  {t.modules.kitchenCumDining.sections.D.hints.deepCleaning.split(":")[0]}:
                </p>
                <ul className="text-sm text-gray-700 list-disc pl-5 space-y-1">
                  <li>Wall tiles/slabs cleaning</li>
                  <li>Ceiling cleaning</li>
                  <li>Cobweb removal</li>
                  <li>Doors and window cleaning</li>
                  <li>Furniture cleaning</li>
                  <li>Daily updated records required</li>
                </ul>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                {[t.global.yes, t.global.no].map((option) => (
                  <label
                    key={option}
                    className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      form.d5 === option
                        ? "bg-blue-100 border-blue-400 shadow-sm"
                        : "bg-white border-gray-200 hover:border-gray-300"
                    } ${errors.d5 ? "border-red-300" : ""}`}
                  >
                    <input
                      type="radio"
                      name="d5"
                      value={option}
                      checked={form.d5 === option}
                      onChange={handleChange}
                      className="mr-3 h-5 w-5"
                      required
                    />
                    <div className="flex items-center">
                      <span
                        className={`text-lg font-medium ${
                          form.d5 === option ? "text-blue-700" : "text-gray-700"
                        }`}
                      >
                        {option}
                      </span>
                    </div>
                  </label>
                ))}
              </div>
              {errors.d5 && (
                <p className="mt-2 text-sm text-red-600 flex items-center">
                  <AlertCircle size={14} className="mr-1" /> {errors.d5}
                </p>
              )}

              {form.d5 === t.global.yes && (
                <div className="mt-6 p-4 bg-green-50 border-2 border-dashed border-green-300 rounded-lg space-y-5">
                  <label className="block text-sm font-medium mb-3 text-gray-700">
                    Upload housekeeping record (Optional):
                  </label>

                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <button
                      type="button"
                      onClick={() => {
                        setCameraField("d5Document");
                        setShowCamera(true);
                      }}
                      disabled={isSubmitting}
                      className="flex items-center justify-center gap-2 p-3 border border-green-300 bg-green-900/10 text-green-900 rounded-lg active:scale-95 active:bg-green-700 active:text-white transition-all duration-150 font-medium disabled:opacity-50"
                    >
                      <Camera size={18} /> {t.global.use_camera}
                    </button>

                    <label
                      htmlFor="d5Document"
                      className={`flex items-center justify-center gap-2 p-3 border border-green-300 bg-green-900/10 text-green-900 rounded-lg active:scale-95 active:bg-green-700 active:text-white transition-all duration-150 cursor-pointer font-medium ${
                        isSubmitting ? "opacity-50 cursor-not-allowed" : ""
                      }`}
                    >
                      <Upload size={18} /> {t.global.upload_file}
                    </label>
                  </div>

                  <input
                    type="file"
                    name="d5Document"
                    id="d5Document"
                    accept=".jpg,.jpeg,.png,.pdf"
                    onChange={handleChange}
                    className="hidden"
                    disabled={isSubmitting}
                  />

                  <div
                    className={`border-2 border-dashed rounded-lg p-6 text-center transition ${
                      form.d5Document
                        ? "border-yellow-400 bg-yellow-50"
                        : "border-gray-300 bg-gray-50"
                    }`}
                  >
                    {form.d5Document ? (
                      <div className="space-y-3">
                        {previewImages.d5Document && (
                          <img
                            src={previewImages.d5Document}
                            alt="Preview"
                            className="max-h-48 mx-auto rounded-lg shadow"
                          />
                        )}

                        <div className="flex items-center justify-between">
                          <p className="text-gray-700 font-medium text-sm break-all">
                            {form.d5Document.name}
                          </p>

                          <button
                            type="button"
                            onClick={() => {
                              setForm((prev) => ({
                                ...prev,
                                d5Document: null,
                              }));
                              setPreviewImages((prev) => ({
                                ...prev,
                                d5Document: null,
                              }));
                            }}
                            className="ml-4 text-red-600 px-3 py-1 rounded-md hover:bg-red-600 hover:text-white transition text-xs font-bold"
                          >
                            {t.global.remove}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <p className="text-gray-400 italic text-sm">
                        {t.global.capture_hint}
                      </p>
                    )}
                  </div>

                  {errors.d5Document && (
                    <p className="mt-2 text-sm text-red-600">
                      {errors.d5Document}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* D6 - Store Area Cleaning */}
            <div className="border rounded-lg p-4">
              <label className="block font-medium mb-3 flex items-center">
                <Package size={18} className="mr-2" />
                D.6 {t.modules.kitchenCumDining.sections.D.questions.storeArea}
                <span className="text-red-500 ml-1">*</span>
              </label>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-gray-800 font-medium mb-1">
                  {t.modules.kitchenCumDining.sections.D.hints.storeArea.split(":")[0]}:
                </p>
                <ul className="text-sm text-gray-700 list-disc pl-5 space-y-1">
                  <li>Weekly deep cleaning of store room</li>
                  <li>Proper maintenance of raw materials storage</li>
                  <li>Daily updated housekeeping checklist</li>
                  <li>Records maintained for verification</li>
                </ul>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                {[t.global.yes, t.global.no].map((option) => (
                  <label
                    key={option}
                    className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      form.d6 === option
                        ? "bg-blue-100 border-blue-400 shadow-sm"
                        : "bg-white border-gray-200 hover:border-gray-300"
                    } ${errors.d6 ? "border-red-300" : ""}`}
                  >
                    <input
                      type="radio"
                      name="d6"
                      value={option}
                      checked={form.d6 === option}
                      onChange={handleChange}
                      className="mr-3 h-5 w-5"
                      required
                    />
                    <div className="flex items-center">
                      <span
                        className={`text-lg font-medium ${
                          form.d6 === option ? "text-blue-700" : "text-gray-700"
                        }`}
                      >
                        {option}
                      </span>
                    </div>
                  </label>
                ))}
              </div>
              {errors.d6 && (
                <p className="mt-2 text-sm text-red-600 flex items-center">
                  <AlertCircle size={14} className="mr-1" /> {errors.d6}
                </p>
              )}
              {form.d6 === t.global.yes && (
                <div className="mt-6 p-4 bg-green-50 border-2 border-dashed border-green-300 rounded-lg space-y-5">
                  <label className="block text-sm font-medium mb-3 text-gray-700">
                    Upload housekeeping record (Optional):
                  </label>

                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <button
                      type="button"
                      onClick={() => {
                        setCameraField("d6Document");
                        setShowCamera(true);
                      }}
                      disabled={isSubmitting}
                      className="flex items-center justify-center gap-2 p-3 border border-green-300 bg-green-900/10 text-green-900 rounded-lg active:scale-95 active:bg-green-700 active:text-white transition-all duration-150 font-medium disabled:opacity-50"
                    >
                      <Camera size={18} /> {t.global.use_camera}
                    </button>

                    <label
                      htmlFor="d6Document"
                      className={`flex items-center justify-center gap-2 p-3 border border-green-300 bg-green-900/10 text-green-900 rounded-lg active:scale-95 active:bg-green-700 active:text-white transition-all duration-150 cursor-pointer font-medium ${
                        isSubmitting ? "opacity-50 cursor-not-allowed" : ""
                      }`}
                    >
                      <Upload size={18} /> {t.global.upload_file}
                    </label>
                  </div>

                  <input
                    type="file"
                    name="d6Document"
                    id="d6Document"
                    accept=".jpg,.jpeg,.png,.pdf"
                    onChange={handleChange}
                    className="hidden"
                    disabled={isSubmitting}
                  />

                  <div
                    className={`border-2 border-dashed rounded-lg p-6 text-center transition ${
                      form.d6Document
                        ? "border-yellow-400 bg-yellow-50"
                        : "border-gray-300 bg-gray-50"
                    }`}
                  >
                    {form.d6Document ? (
                      <div className="space-y-3">
                        {previewImages.d6Document && (
                          <img
                            src={previewImages.d6Document}
                            alt="Preview"
                            className="max-h-48 mx-auto rounded-lg shadow"
                          />
                        )}

                        <div className="flex items-center justify-between">
                          <p className="text-gray-700 font-medium text-sm break-all">
                            {form.d6Document?.name}
                          </p>

                          <button
                            type="button"
                            onClick={() => {
                              setForm((prev) => ({
                                ...prev,
                                d6Document: null,
                              }));
                              setPreviewImages((prev) => ({
                                ...prev,
                                d6Document: null,
                              }));
                            }}
                            className="ml-4 text-red-600 px-3 py-1 rounded-md hover:bg-red-600 hover:text-white transition text-xs font-bold"
                          >
                            {t.global.remove}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <p className="text-gray-400 italic text-sm">
                        {t.global.capture_hint}
                      </p>
                    )}
                  </div>

                  {errors.d6Document && (
                    <p className="mt-2 text-sm text-red-600">
                      {errors.d6Document}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* D7 - LPG Usage */}
            <div className="border rounded-lg p-4 bg-blue-50 border-blue-200">
              <label className="block font-medium mb-3 flex items-center">
                <Flame size={18} className="mr-2" />
                D.7 {t.modules.kitchenCumDining.sections.D.questions.fuelType}
                <span className="text-red-500 ml-1">*</span>
              </label>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[t.global.yes, t.global.no].map((option) => (
                  <label
                    key={option}
                    className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      form.d7 === option
                        ? "bg-blue-100 border-blue-400 shadow-sm"
                        : "bg-white border-gray-200 hover:border-gray-300"
                    } ${errors.d7 ? "border-red-300" : ""}`}
                  >
                    <input
                      type="radio"
                      name="d7"
                      value={option}
                      checked={form.d7 === option}
                      onChange={handleChange}
                      className="mr-3 h-5 w-5"
                      required
                    />
                    <div className="flex items-center">
                      <span
                        className={`text-lg font-medium ${
                          form.d7 === option ? "text-blue-700" : "text-gray-700"
                        }`}
                      >
                        {option}
                      </span>
                    </div>
                  </label>
                ))}
              </div>
              {errors.d7 && (
                <p className="mt-2 text-sm text-red-600 flex items-center">
                  <AlertCircle size={14} className="mr-1" /> {errors.d7}
                </p>
              )}
            </div>

            {/* D8 - Cleanliness Rating */}
            <div className="border rounded-lg p-4">
              <label className="block font-medium mb-3 flex items-center">
                <Sparkles size={18} className="mr-2" />
                D.8 {t.modules.kitchenCumDining.sections.D.questions.overallCleanliness}
                <span className="text-red-500 ml-1">*</span>
              </label>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {ratingOptions.map((option) => (
                  <label
                    key={option}
                    className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      form.d8 === option
                        ? option === t.global.rating_good
                          ? "bg-green-100 border-green-400"
                          : option === t.global.rating_satisfactory
                            ? "bg-yellow-100 border-yellow-400"
                            : "bg-red-100 border-red-400"
                        : "bg-white border-gray-200 hover:border-gray-300"
                    } ${errors.d8 ? "border-red-300" : ""}`}
                  >
                    <input
                      type="radio"
                      name="d8"
                      value={option}
                      checked={form.d8 === option}
                      onChange={handleChange}
                      className="mr-3 h-5 w-5"
                      required
                    />
                    <div className="flex items-center">
                      <span
                        className={`text-lg font-medium ${
                          form.d8 === option
                            ? option === t.global.rating_good
                              ? "text-green-700"
                              : option === t.global.rating_satisfactory
                                ? "text-yellow-700"
                                : "text-red-700"
                            : "text-gray-700"
                        }`}
                      >
                        {option}
                      </span>
                    </div>
                  </label>
                ))}
              </div>
              {errors.d8 && (
                <p className="mt-2 text-sm text-red-600 flex items-center">
                  <AlertCircle size={14} className="mr-1" /> {errors.d8}
                </p>
              )}
            </div>

            {/* Footer Info */}
            <div className="pt-4 border-t border-gray-200">
              <div className="text-center text-sm text-gray-500">
                <span className="text-red-500 font-medium">
                  * Required fields must be completed
                </span>
                <p className="text-sm text-gray-600 mt-1">
                  All other information is optional but recommended for comprehensive kitchen and dining assessment.
                </p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-between pt-6 border-t">
              <button
                type="button"
                onClick={() => navigate("/survey/sections", { state })}
                className="px-6 py-3 bg-gray-200 rounded-lg hover:bg-gray-300 transition font-medium flex items-center"
                disabled={isSubmitting}
              >
                <ArrowLeft size={18} className="mr-2" />
                {t.global.back_to_sections}
              </button>

              <button
                type="button"
                onClick={handleSubmit}
                disabled={isSubmitting}
                className={`px-6 py-3 rounded-lg font-medium flex items-center transition ${
                  isSubmitting
                    ? "bg-blue-400 cursor-not-allowed"
                    : "bg-blue-600 text-white hover:bg-blue-700"
                }`}
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    {t.global.submitting}
                  </>
                ) : (
                  <>
                    <Save size={18} className="mr-2" />
                    {t.global.submit_section_d}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Custom Animation */}
      <style jsx>{`
        @keyframes scale-in {
          from {
            transform: scale(0.9);
            opacity: 0;
          }
          to {
            transform: scale(1);
            opacity: 1;
          }
        }
        .animate-scale-in {
          animation: scale-in 0.3s ease-out;
        }
      `}</style>
      {showCamera && (
        <CameraCapture
          onCapture={(file) => {
            setForm((prev) => ({
              ...prev,
              [cameraField]: file,
            }));

            setPreviewImages((prev) => ({
              ...prev,
              [cameraField]: URL.createObjectURL(file),
            }));

            setShowCamera(false);
          }}
          onClose={() => setShowCamera(false)}
        />
      )}
    </div>
  );
};

export default SectionDKitchenDining;